import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { EventsApiService } from './events-api.service';
import {
  EventsActionDeleteEvent,
  EventsActionDeleteEventFailed,
  EventsActionDeleteEventSuccess,
  EventsActionLoadCategoriesFailed,
  EventsActionLoadCategoriesSuccess,
  EventsActionLoadEventsFailed,
  EventsActionLoadEventsSuccess,
  EventsActions,
  EventsActionSaveEvent,
  EventsActionSaveEventFailed,
  EventsActionSaveEventSuccess,
  EventsActionTypes,
  EventsActionUpdateEvent,
  EventsActionUpdateEventFailed,
  EventsActionUpdateEventSuccess
} from './events.actions';

@Injectable()
export class EventsEffects {
  private actions$ = inject(Actions);
  private eventsApiService = inject(EventsApiService);

  loadEvents$: Observable<EventsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActionTypes.LOAD_EVENTS),
      switchMap(() =>
        this.eventsApiService.loadEvents().pipe(
          map(res => new EventsActionLoadEventsSuccess(res)),
          catchError(() => of(new EventsActionLoadEventsFailed()))
        )
      )
    )
  );

  loadCategories$: Observable<EventsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActionTypes.LOAD_CATEGORIES),
      switchMap(() =>
        this.eventsApiService.loadCategories().pipe(
          map(res => new EventsActionLoadCategoriesSuccess(res)),
          catchError(() => of(new EventsActionLoadCategoriesFailed()))
        )
      )
    )
  );

  saveEvent$: Observable<EventsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActionTypes.SAVE_EVENT),
      map((action: EventsActionSaveEvent) => action.payload),
      switchMap(payload =>
        this.eventsApiService
          .saveEvent(
            payload.categoryUuid,
            payload.datetime,
            payload.value,
            payload.reminder
          )
          .pipe(
            map(() => new EventsActionSaveEventSuccess()),
            catchError(() => of(new EventsActionSaveEventFailed()))
          )
      )
    )
  );

  deleteEvent$: Observable<EventsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActionTypes.DELETE_EVENT),
      map((action: EventsActionDeleteEvent) => action.payload),
      switchMap(payload =>
        this.eventsApiService.deleteEvent(payload).pipe(
          map(() => new EventsActionDeleteEventSuccess()),
          catchError(() => of(new EventsActionDeleteEventFailed()))
        )
      )
    )
  );

  updateEvent$: Observable<EventsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActionTypes.UPDATE_EVENT),
      map((action: EventsActionUpdateEvent) => action.payload),
      switchMap(payload =>
        this.eventsApiService
          .updateEvent(
            payload.eventUuid,
            payload.datetime,
            payload.reminder,
            payload.value,
            payload.attended
          )
          .pipe(
            map(() => new EventsActionUpdateEventSuccess()),
            catchError(() => of(new EventsActionUpdateEventFailed()))
          )
      )
    )
  );
}
